import React from 'react';

// Fallbacks only; both are served on every block. The server marks a target
// response later than isi_ms INCORRECT, so a client window longer than the
// served one would let the participant answer outside the window the ladder is
// escalating. `n` saturates at 4, after which isi_ms is the ONLY parameter still
// moving, so a client that ignores it serves an identical game forever while the
// level counter climbs.
const DEFAULT_ISI_MS = 3000;
const DEFAULT_N = 1;

// Coerced through Number() at every use because the runner derives its
// reaction-time ceiling as `timing().windowMs + 250`: a string window would
// concatenate ("1400" + 250 -> "1400250") and every late response would be
// accepted as a real one. `||` rather than `??` so a served 0 falls back to the
// default instead of becoming an instant timeout that records the whole block as
// non-responses.
const nOf = (params) => Number(params?.n) || DEFAULT_N;

// Duration of the onset cue. Long enough to be unmissable, short enough that
// the next letter (isi_ms is 3000ms at the easiest rung and never approaches
// this) is never still animating when it arrives.
const ONSET_MS = 260;

// This component is handed the stimulus and the trial — no params, no block, no
// history — so it cannot tell whether this letter matches the one n back, which
// makes the invariant structural rather than a matter of care. `trial` is used
// for its INDEX only; nothing else on it is read. The accessible name says which
// letter is on screen, which is exactly what a sighted participant is given; it
// must never say whether that letter is a match.
//
// The onset cue is why the index is needed. This is a continuous stream —
// fixationMs and gapMs are both 0, deliberately (see `timing` below) — so
// between two trials nothing blanks, nothing moves, and React repaints a text
// node whose text has not changed. When a letter repeats, the screen is
// therefore PIXEL-IDENTICAL across the trial boundary and the participant has
// no way to know a new letter arrived. On a 1-back block that is not a polish
// problem: a match IS a repeat by definition, so the trials being scored are
// exactly the ones that cannot be seen to arrive.
//
// The cue must not be a blank gap. The server grades a response later than
// isi_ms as incorrect and isi_ms IS the response window, so any dark interval
// would come out of the time the participant is allowed to answer in. It must
// also not touch the letter's contrast or position, both of which would show up
// as reaction time. What is left is a transient that runs alongside a letter
// already at full opacity from its first frame: a fast scale settle, and a rule
// beneath the letter that wipes out and fades. Both are keyed on the trial
// index, which is what makes them replay on a repeat — a re-render alone would
// not, since nothing about the letter changed.
function StimulusView({ stimulus, trial }) {
  const key = trial?.trialIndex ?? 0;
  return (
    <div style={{ display: 'grid', placeItems: 'center', position: 'relative' }}>
      {/* The animations are applied by CLASS, not inline. A reduced-motion
          override has to beat them, and nothing in a stylesheet beats an inline
          style attribute — the reduced-motion rules below would silently do
          nothing. Redefining the same @keyframes name inside the media block is
          not an alternative: it was tried, and which definition wins is not
          something to rest an accessibility guarantee on. */}
      <style>{`
        .nb-onset-letter { animation: nb-onset-letter ${ONSET_MS}ms cubic-bezier(0.2, 0.9, 0.25, 1); }
        .nb-onset-rule { animation: nb-onset-rule ${ONSET_MS}ms ease-out forwards; }
        @keyframes nb-onset-letter { from { transform: scale(0.88); } to { transform: scale(1); } }
        @keyframes nb-onset-rule { from { transform: scaleX(0.1); opacity: 1; } to { transform: scaleX(1); opacity: 0; } }
        @keyframes nb-onset-rule-reduced { from { opacity: 1; } to { opacity: 0; } }
        /* Reduced motion still needs an onset signal — drop it entirely and the
           repeat trials go back to being invisible, which is the whole problem.
           So the movement goes and the signal stays: the letter does not scale,
           and the rule fades in place instead of wiping. */
        @media (prefers-reduced-motion: reduce) {
          .nb-onset-letter { animation: none; }
          .nb-onset-rule { animation-name: nb-onset-rule-reduced; }
        }
      `}</style>
      <span
        key={`letter-${key}`}
        className="nb-onset-letter"
        role="img"
        aria-label={`letter ${stimulus.letter}`}
        style={{
          fontSize: 'clamp(96px, 24vw, 200px)',
          fontWeight: 800,
          lineHeight: 0.9,
          letterSpacing: '-0.04em',
          color: 'var(--text-primary)',
        }}
      >
        {stimulus.letter}
      </span>
      <div
        key={`rule-${key}`}
        className="nb-onset-rule"
        data-testid="nb-onset"
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -8,
          width: '58%',
          height: 4,
          borderRadius: 'var(--radius-full)',
          background: 'var(--accent-primary)',
        }}
      />
    </div>
  );
}

// `n` IS shown, deliberately and continuously. The rule is public — "does this
// match the one n back" — and a participant who has to remember n is being
// measured on something else. It is not a difficulty knob to be hidden the way
// go-no-go's target_rate is: knowing n tells you nothing about whether THIS
// letter is a match. Nothing else off params reaches the screen, and this is
// training, so nothing resembling a score may appear here either.
function BlockContextView({ params }) {
  return (
    <div
      style={{
        padding: 'var(--space-2) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-strong)',
        background: 'var(--bg-elevated)',
        color: 'var(--text-primary)',
        fontSize: 'var(--text-body-size)',
        fontWeight: 700,
      }}
    >
      Match the letter {nOf(params)} back
    </div>
  );
}

export const nBackTask = {
  id: 'n-back',
  name: 'Letter Echo',
  pillar: 'memory',

  // Load-bearing, twice over. Doing nothing IS the correct answer on a
  // non-match, so ending on the press would make match trials visibly shorter
  // and let the rule be read off the pacing. And this window IS isi_ms — the
  // interval between letters in a continuous stream, and the ONLY parameter
  // still moving once n saturates at 4 — so cutting it short both varies the
  // maintenance demand trial to trial and removes the ladder's last knob.
  holdsFullWindow: true,

  instructions: [
    'Letters appear one at a time.',
    'Press when the letter matches the one a few back — the panel tells you how many.',
    'Do nothing when it does not match.',
    'Answer while the letter is still on screen; a late press does not count.',
  ],
  targets: [{ id: 'match', label: 'Match', keys: [' '], hint: 'space' }],

  // Empty deliberately. TaskExamples renders one stimulus per row, and this
  // task's lesson is a relation BETWEEN two frames — a single letter in
  // isolation illustrates nothing. TaskExamples renders null for an empty list.
  examples: [],

  // One target, so targetId carries no information. A withhold never reaches
  // this function — the runner records it as an all-null trial, which is the
  // correct answer on a non-match.
  toAnswer: () => ({ pressed: true }),

  // The server enforces isi_ms when grading a target response, so the client
  // window must equal it exactly — not isi_ms minus a fixation, not a constant.
  // fixationMs and gapMs are both 0: this is a continuous stream, and a cross or
  // a blank between letters would change the maintenance demand the instrument
  // measures. Flanker and stroop fit a fixation inside the served isi and
  // pattern-comparison stacks one on top of it; those conventions are mandated
  // per-instrument and must not be carried over here.
  timing: (params) => ({
    fixationMs: 0,
    windowMs: Number(params?.isi_ms) || DEFAULT_ISI_MS,
    gapMs: 0,
  }),

  // Computes nothing hidden. The whole stream is served, and every letter this
  // comparison needs has ALREADY BEEN ON SCREEN — reading it back out of
  // blockTrials is exactly what the participant is asked to do from memory. Only
  // indices strictly before the current one are read; no served field says
  // whether a trial is a target, and none is consulted.
  practiceExpectation: (stimulus, ctx) => {
    const n = nOf(ctx?.params);
    const trials = ctx?.blockTrials;
    const index = ctx?.trial?.trialIndex;
    if (!Array.isArray(trials) || typeof index !== 'number') return null;
    if (index < n) return null; // nothing n back yet
    const back = trials[index - n]?.stimulus?.letter;
    return back === stimulus.letter ? 'match' : null;
  },

  StimulusView,
  BlockContextView,
};
